import { useEffect, useState } from 'react';
import { useAuth } from '@/lib/use-auth';
import { ApiError } from '@/lib/api';
import { fetchMyReferralCode, applyReferralCode, fetchMyReferrals } from '@/lib/referral-api';
import type { ReferralResponse } from '@/lib/referral-api';
import { Button } from '@/components/ui/button';
import {
  Sparkles,
  Gift,
  Copy,
  Check,
  Ticket,
  CheckCircle2,
  AlertCircle,
  Users,
  Clock
} from 'lucide-react';

export function ReferralsPage() {
  const { accessToken } = useAuth();
  const [code, setCode] = useState<string | null>(null);
  const [referrals, setReferrals] = useState<ReferralResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [applyInput, setApplyInput] = useState('');
  const [applyBusy, setApplyBusy] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [applyDone, setApplyDone] = useState(false);

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    Promise.all([fetchMyReferralCode(accessToken), fetchMyReferrals(accessToken)])
      .then(([c, r]) => {
        if (!cancelled) {
          setCode(c);
          setReferrals(r);
          setLoading(false);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof ApiError ? e.message : 'Failed to load referrals');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  async function handleCopy() {
    if (!code) return;
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function handleApply(e: React.FormEvent) {
    e.preventDefault();
    if (!accessToken || !applyInput.trim()) return;
    setApplyBusy(true);
    setApplyError(null);
    try {
      await applyReferralCode(accessToken, applyInput.trim().toUpperCase());
      setApplyDone(true);
      setApplyInput('');
    } catch (err) {
      setApplyError(err instanceof ApiError ? err.message : 'Failed to apply code');
    } finally {
      setApplyBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto py-4 space-y-8 animate-pulse">
        {/* Header Hero Section Skeleton */}
        <div className="relative overflow-hidden rounded-2xl bg-slate-200 dark:bg-slate-800/50 p-8 shadow-lg h-[184px]">
          <div className="relative z-10 max-w-xl space-y-3.5">
            <div className="h-5 w-36 rounded-full bg-slate-300 dark:bg-slate-700/60" />
            <div className="h-8 w-64 rounded-lg bg-slate-300 dark:bg-slate-700/60" />
            <div className="h-4 w-full rounded-lg bg-slate-300 dark:bg-slate-700/60" />
          </div>
        </div>

        {/* Main Grid Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {/* Left Column: Code and Apply code */}
          <div className="md:col-span-7 space-y-6">
            
            {/* Card 1: Your Referral Code Skeleton */}
            <div className="bg-white dark:bg-slate-900/60 p-6 rounded-2xl shadow-sm space-y-5">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-slate-200 dark:bg-slate-800" />
                <div className="space-y-2">
                  <div className="h-4 w-32 rounded bg-slate-200 dark:bg-slate-800" />
                  <div className="h-3 w-48 rounded bg-slate-200 dark:bg-slate-800" />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
                <div className="w-full sm:w-64 h-10 rounded-xl bg-slate-100 dark:bg-slate-800/40" />
                <div className="w-full sm:w-36 h-10 rounded-xl bg-slate-200 dark:bg-slate-800" />
              </div>
            </div>

            {/* Card 2: Apply referral code Skeleton */}
            <div className="bg-white dark:bg-slate-900/60 p-6 rounded-2xl shadow-sm space-y-5">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-slate-200 dark:bg-slate-800" />
                <div className="space-y-2">
                  <div className="h-4 w-36 rounded bg-slate-200 dark:bg-slate-800" />
                  <div className="h-3 w-64 rounded bg-slate-200 dark:bg-slate-800" />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
                <div className="flex-1 h-10 rounded-xl bg-slate-100 dark:bg-slate-800/40" />
                <div className="w-full sm:w-36 h-10 rounded-xl bg-slate-200 dark:bg-slate-800" />
              </div>
            </div>

          </div>

          {/* Right Column: Referral History Skeleton */}
          <div className="md:col-span-5 flex flex-col">
            <div className="flex-1 flex flex-col bg-white dark:bg-slate-900/60 p-6 rounded-2xl shadow-sm space-y-5">
              <div className="flex items-center justify-between gap-4 pb-3">
                <div className="flex items-center gap-2.5">
                  <div className="h-5 w-5 rounded bg-slate-200 dark:bg-slate-800" />
                  <div className="h-4 w-28 rounded bg-slate-200 dark:bg-slate-800" />
                </div>
                <div className="h-5 w-16 rounded-full bg-slate-200 dark:bg-slate-800" />
              </div>

              <div className="flex-1 space-y-3.5 pt-1">
                {Array.from({ length: 3 }).map((_, idx) => (
                  <div key={idx} className="flex items-center justify-between rounded-xl bg-slate-50/50 dark:bg-slate-900/40 px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-slate-200 dark:bg-slate-800" />
                      <div className="space-y-1.5">
                        <div className="h-3.5 w-24 rounded bg-slate-200 dark:bg-slate-800" />
                        <div className="h-2.5 w-16 rounded bg-slate-200 dark:bg-slate-800" />
                      </div>
                    </div>
                    <div className="h-3 w-16 rounded bg-slate-200 dark:bg-slate-800" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-4 space-y-8">
      {/* Header Hero Section */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-brand-600 via-brand-700 to-accent-600 p-8 text-white shadow-lg">
        {/* Cosmos decorative shapes */}
        <div className="absolute top-0 right-0 -mt-6 -mr-6 h-36 w-36 rounded-full bg-white/10 blur-xl animate-pulse" />
        <div className="absolute bottom-0 left-1/4 -mb-10 h-44 w-44 rounded-full bg-accent-500/20 blur-2xl" />
        
        <div className="relative z-10 max-w-xl space-y-3">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold backdrop-blur-md">
            <Sparkles className="h-3.5 w-3.5 text-amber-300 fill-amber-300" />
            <span>CosmoBud Referral Program</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-black tracking-tight">Refer &amp; Grow Together</h1>
          <p className="text-sm text-slate-100 font-medium leading-relaxed">
            Invite your friends to experience the wonder of personalized education. Share your unique code and build a community of curious minds!
          </p>
        </div>
      </div>

      {error && (
        <div role="alert" className="rounded-xl border border-red-200 bg-red-50/50 p-4 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/20 dark:text-red-400 flex items-center gap-2">
          <AlertCircle className="h-4.5 w-4.5 text-red-500 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Main Grid */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
        {/* Left Column: Code and Apply code */}
        <div className="md:col-span-7 space-y-6">
          
          {/* Card 1: Your Referral Code */}
          <div className="bg-white dark:bg-slate-900/60 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 dark:bg-brand-950/40 border border-brand-100/30 dark:border-brand-900/30 text-brand-600 dark:text-brand-400">
                <Gift className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">Your Referral Code</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400">Copy and share this code with friends</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
              <div className="w-full sm:w-64 flex items-center justify-center rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/20 px-3.5 py-2.5">
                <span className="font-mono text-base font-black tracking-widest text-slate-950 dark:text-slate-50 text-center">
                  {code ?? '—'}
                </span>
              </div>
              
              <Button
                onClick={() => void handleCopy()}
                disabled={!code}
                className="h-10 w-full sm:w-36 shrink-0 font-bold text-xs bg-slate-900 hover:bg-slate-800 dark:bg-slate-800 dark:hover:bg-slate-700 text-white rounded-xl px-5 flex items-center justify-center gap-2 cursor-pointer shadow-sm active:scale-98 transition-all"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4 text-emerald-400 animate-scale-up" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    <span>Copy Code</span>
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Card 2: Apply referral code */}
          <div className="bg-white dark:bg-slate-900/60 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent-50 dark:bg-accent-950/40 border border-accent-100/30 dark:border-accent-900/30 text-accent-600 dark:text-accent-400">
                <Ticket className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">Apply Referral Code</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400">Enter a code received from a friend to link the referral</p>
              </div>
            </div>

            {applyDone ? (
              <div className="flex items-center gap-2.5 p-3.5 bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900/30 rounded-xl text-sm text-emerald-700 dark:text-emerald-400 font-semibold animate-scale-up">
                <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />
                <span>Referral code applied successfully!</span>
              </div>
            ) : (
              <form onSubmit={(e) => void handleApply(e)} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-1">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={applyInput}
                    onChange={(e) => setApplyInput(e.target.value)}
                    placeholder="e.g. AB12CD34"
                    maxLength={20}
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white focus:border-brand-500 dark:bg-slate-800/40 dark:border-slate-800 dark:focus:border-brand-400 px-3.5 text-xs font-mono uppercase focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:text-slate-100 placeholder-slate-450 transition-all font-bold tracking-wider"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={applyBusy || !applyInput.trim()}
                  className="h-10 w-full sm:w-36 shrink-0 font-bold text-xs bg-brand-600 hover:bg-brand-700 dark:bg-brand-500 dark:hover:bg-brand-600 text-white rounded-xl px-5 flex items-center justify-center gap-2 cursor-pointer shadow-sm shadow-brand-500/10 active:scale-98 transition-all"
                >
                  {applyBusy ? 'Applying…' : 'Apply Code'}
                </Button>
              </form>
            )}
            {applyError && (
              <p className="text-xs text-red-600 dark:text-red-400 font-medium pl-1 flex items-center gap-1.5 animate-pulse">
                <AlertCircle className="h-3.5 w-3.5 text-red-500" />
                <span>{applyError}</span>
              </p>
            )}
          </div>

        </div>

        {/* Right Column: Referral History */}
        <div className="md:col-span-5 flex flex-col">
          <div className="flex-1 flex flex-col bg-white dark:bg-slate-900/60 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
            <div className="flex items-center justify-between gap-4 border-b border-slate-100 dark:border-slate-800 pb-3">
              <div className="flex items-center gap-2.5">
                <Users className="h-5 w-5 text-brand-500" />
                <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">Referral History</h2>
              </div>
              <span className="inline-flex items-center justify-center rounded-full bg-brand-50 dark:bg-brand-950/40 border border-brand-100/20 px-2.5 py-0.5 text-xs font-bold text-brand-600 dark:text-brand-400">
                {referrals.length} referred
              </span>
            </div>

            {referrals.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center py-12 px-4 space-y-3.5">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-50 dark:bg-slate-900/40 text-slate-400 border border-dashed border-slate-200 dark:border-slate-800">
                  <Users className="h-5 w-5" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200">No referrals yet</h3>
                  <p className="text-xs text-slate-400 dark:text-slate-500 max-w-[200px] leading-relaxed">
                    Share your code with friends to get started on your journey together.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto max-h-[380px] pr-1 pt-1">
                <ul className="space-y-3.5">
                  {referrals.map((r) => (
                    <li
                      key={r.id}
                      className="flex items-center justify-between rounded-xl border border-slate-150 dark:border-slate-800/80 bg-slate-50/30 dark:bg-slate-900/20 px-4 py-3 text-sm shadow-3xs"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-50 dark:bg-brand-950/40 border border-brand-100/10 text-xs font-bold text-brand-600 dark:text-brand-400">
                          {r.referredId.slice(-2).toUpperCase()}
                        </div>
                        <div className="space-y-0.5">
                          <span className="font-mono text-xs font-bold text-slate-800 dark:text-slate-200">
                            Member #{r.referredId.slice(-6).toUpperCase()}
                          </span>
                          <div className="flex items-center gap-1 text-[10px] text-slate-400 dark:text-slate-500 font-medium">
                            <Clock className="h-2.5 w-2.5" />
                            <span>Linked successfully</span>
                          </div>
                        </div>
                      </div>
                      <span className="text-xs font-semibold text-slate-555 dark:text-slate-400">
                        {new Date(r.createdAt).toLocaleDateString(undefined, {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
